import { ArticleResolution } from '@wix/ambassador-cc-kb-server-v1-article-translation/types';
import { Box, Text, ToggleButton } from '@wix/design-system';
import { useTranslation } from '@wix/wix-i18n-config';
import { Heart, HeartFilled, ThumbsUp, ThumbsUpFilled } from '@wix/wix-ui-icons-common';
import React, { FC, useCallback, useMemo } from 'react';
import { useStore } from 'react-redux';

import { TLocalesKeys } from '../../../../types';
import { ButtonClicked, logArticleVoteButtonClick } from '../../../bi';
import { HelpCenterVoteableArticle } from '../../types';
import { articleVoteButtonDataHooks } from './ArticleVoteButton.dataHooks';

export interface FeatureRequestVoteButtonProps {
  article: HelpCenterVoteableArticle;
  onVote: () => void;
  isVoted: boolean;
  isVotedInCurrentSession: boolean;
}

const getButtonClicked = (isVoted: boolean, isCollectingVotes: boolean) => {
  if (isCollectingVotes) {
    return isVoted ? ButtonClicked.FeatureRequestVote : ButtonClicked.FeatureRequestUnvote;
  }

  return isVoted ? ButtonClicked.FeatureRequestFollow : ButtonClicked.FeatureRequestUnfollow;
};

export const FeatureRequestVoteButton: FC<FeatureRequestVoteButtonProps> = ({
  article,
  onVote,
  isVoted,
  isVotedInCurrentSession,
}) => {
  const store = useStore();
  const { t } = useTranslation();

  const isCollectingVotes = article.resolution === ArticleResolution.COLLECTING_VOTES;

  const onClick = useCallback(() => {
    const newVotedValue = !isVoted;

    logArticleVoteButtonClick(store.getState(), {
      articleId: article.id,
      buttonClicked: getButtonClicked(newVotedValue, isCollectingVotes),
    });

    onVote();
  }, [article, onVote, isVoted, store, isCollectingVotes]);

  const buttonTextKey = useMemo<TLocalesKeys>(() => {
    if (isCollectingVotes) {
      return isVoted
        ? 'article_footer_unvote_for_feature_request_btn'
        : 'article_footer_vote_for_feature_request_btn';
    }
    return isVoted
      ? 'article_footer_unfollow_feature_request_btn'
      : 'article_footer_follow_feature_request_btn';
  }, [isCollectingVotes, isVoted]);

  const thanksTextKey = useMemo<TLocalesKeys | null>(() => {
    if (isVotedInCurrentSession) {
      return isCollectingVotes
        ? 'article_footer_feature_request_thanks_text'
        : 'article_footer_feature_request_follow_thanks_text';
    }

    return null;
  }, [isVotedInCurrentSession, isCollectingVotes]);

  const icon = useMemo(() => {
    if (isCollectingVotes) {
      return isVoted ? <ThumbsUpFilled /> : <ThumbsUp />;
    }
    return isVoted ? <HeartFilled /> : <Heart />;
  }, [isCollectingVotes, isVoted]);

  return (
    <Box direction="vertical" gap="SP4">
      <Box direction="vertical" gap="SP2">
        <Box>
          <ToggleButton
            shape="pill"
            border
            selected={isVoted}
            onClick={onClick}
            labelValue={
              // Have to use span because ToggleButton driver has an issue with .click()
              <span data-hook={articleVoteButtonDataHooks.featureRequestVoteButton()}>
                {t<TLocalesKeys>(buttonTextKey)}
              </span>
            }
          >
            {icon}
          </ToggleButton>
        </Box>
        {thanksTextKey ? (
          <Text weight="normal" size="small">
            {t<TLocalesKeys>(thanksTextKey)}
          </Text>
        ) : null}
      </Box>
      <Text size="tiny">
        <Box color="D40">{t<TLocalesKeys>('article_footer_feature_request_note_text')}</Box>
      </Text>
    </Box>
  );
};
